class SearchDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get shouldAppendToBody() {
    return false;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);

class SearchTyped extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get startDelay() {
    return parseFloat(this.dataset.delay || 0);
  }

  async init() {
    this.insertCursor();
    await this.start(this.dataset.firstText, this.startDelay);

    setTimeout(async () => {
      await this.reset();
      await this.start(this.dataset.lastText, 0);
    }, 600);
  }

  async start(text, delay) {
    this.innerHTML = text;
    await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
    this.cursor.classList.add('blink');
  }

  async reset() {
    this.cursor.classList.remove('blink');
    await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
  }

  insertCursor() {
    if (this.cursor) return;

    this.cursor = document.createElement('span');
    this.cursor.className = 'typed-cursor';
    this.cursor.setAttribute('aria-hidden', true);
    this.cursor.innerHTML = '|';
    this.parentElement.insertBefore(this.cursor, this.nextSibling);
  }
}
customElements.define('search-typed', SearchTyped);

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();

    this.cachedMap = new Map();
    this.focusElement = this.input;

    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('click', this.hideTypewriter.bind(this));
    this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get typewriter() {
    return this.input.previousElementSibling;
  }

  get useFullSearchProducts() {
    return this.hasAttribute('data-use-full-search-products');
  }

  hideTypewriter() {
    if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
      this.typewriter.setAttribute('hidden', '');
    }
  }

  onFocus(event) {
    if (this.closest('.collection')) {
      document.body.classList.add('predictive-search-open');

      const searchTerm = this.getQuery();
      if (searchTerm.length === 0) return;

      if (this.useFullSearchProducts) {
        this.renderMergedFromFetch();
      } else {
        const url = this.buildUrl().toString();
        this.renderSection(url, event);
      }
    }
  }

  clear(event = null) {
    if (event) event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.cachedMap.clear();
  }

  getQuery() {
    return this.input.value.trim();
  }

  suggestLimit() {
    const n = parseInt(this.dataset.limit || '10', 10);
    return Math.min(Number.isFinite(n) ? n : 10, 10);
  }

  findInParsedDoc(doc, exactId, idPrefix) {
    let el = doc.getElementById(exactId);
    if (!el) {
      try {
        el = doc.querySelector(`#${CSS.escape(exactId)}`);
      } catch {
        el = null;
      }
    }
    if (!el && idPrefix) {
      el = doc.querySelector(`[id^="${idPrefix}"]`);
    }
    return el;
  }

  buildUrl() {
    const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[limit]', String(this.suggestLimit()));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set(
      'section_id',
      this.dataset.predictiveApiSectionId || theme.utils.sectionId(this)
    );

    if (this.useFullSearchProducts && this.dataset.suggestResourceTypes) {
      url.searchParams.set('resources[type]', this.dataset.suggestResourceTypes);
    }

    return url;
  }

  buildFullSearchProductsUrl() {
    const url = new URL(`${theme.routes.shop_url}${theme.routes.search_url}`);
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', this.dataset.fullSearchSectionId || 'ck-predictive-search-products');
    url.searchParams.set('options[prefix]', 'last');
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }

    this.hideTypewriter();

    if (this.useFullSearchProducts) {
      this.renderMergedFromFetch();
    } else {
      const url = this.buildUrl().toString();
      this.renderSection(url);
    }
  }

  renderSection(url) {
    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);

    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setAttribute('loading', '');

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);

        this.removeAttribute('loading');
        this.setAttribute('results', '');
      });
  }

  renderSearchResults(responseText) {
    const sid = theme.utils.sectionId(this);
    if (!sid) return;

    const id = 'PredictiveSearchResults-' + sid;
    const liveEl = document.getElementById(id);
    if (!liveEl) return;

    const doc = new DOMParser().parseFromString(responseText, 'text/html');
    const srcEl = this.findInParsedDoc(doc, id, 'PredictiveSearchResults-');
    if (!srcEl) return;

    liveEl.innerHTML = srcEl.innerHTML;
  }

  mergedCacheKey() {
    return `${this.buildUrl().toString()}\n${this.buildFullSearchProductsUrl().toString()}`;
  }

  renderMergedFromFetch() {
    const cacheKey = this.mergedCacheKey();
    if (this.cachedMap.has(cacheKey)) {
      const { suggestText, fullText } = this.cachedMap.get(cacheKey);
      this.applyMergedResults(suggestText, fullText);
      this.setAttribute('results', '');
      return;
    }

    this.setAttribute('loading', '');

    const suggestUrl = this.buildUrl().toString();
    const fullUrl = this.buildFullSearchProductsUrl().toString();

    Promise.allSettled([
      fetch(suggestUrl).then((r) => r.text()),
      fetch(fullUrl).then((r) => r.text()),
    ])
      .then(([suggestResult, fullResult]) => {
        const suggestText = suggestResult.status === 'fulfilled' ? suggestResult.value : '';
        const fullText = fullResult.status === 'fulfilled' ? fullResult.value : '';
        this.cachedMap.set(cacheKey, { suggestText, fullText });
        this.applyMergedResults(suggestText, fullText);
        this.removeAttribute('loading');
        this.setAttribute('results', '');
      })
      .catch(() => {
        this.removeAttribute('loading');
      });
  }

  applyMergedResults(suggestText, fullText) {
    const sid = theme.utils.sectionId(this);
    const parser = new DOMParser();

    const suggestDoc = parser.parseFromString(suggestText, 'text/html');
    const nonProductsId = 'PredictiveSearchNonProducts-' + sid;
    const liveNon = document.getElementById(nonProductsId);
    const srcNon = this.findInParsedDoc(suggestDoc, nonProductsId, 'PredictiveSearchNonProducts-');
    if (liveNon && srcNon) {
      liveNon.innerHTML = srcNon.innerHTML;
    }

    const collectionsId = 'PredictiveSearchCollections-' + sid;
    const liveCol = document.getElementById(collectionsId);
    const srcCol = this.findInParsedDoc(suggestDoc, collectionsId, 'PredictiveSearchCollections-');
    if (liveCol && srcCol) {
      liveCol.innerHTML = srcCol.innerHTML;
    }

    const productsTarget = document.querySelector(`#PredictiveSearchProducts-${sid} [data-ck-predictive-products-target]`);
    const fullDoc = parser.parseFromString(fullText, 'text/html');
    const inner = fullDoc.querySelector('[data-ck-predictive-products-inner]');
    if (productsTarget && inner) {
      productsTarget.innerHTML = inner.innerHTML;
    }

    this.updatePredictiveMeta(sid);
  }

  updatePredictiveMeta(sid) {
    const results = document.getElementById('PredictiveSearchResults-' + sid);
    if (!results) return;

    const non = document.getElementById('PredictiveSearchNonProducts-' + sid);
    const prodTarget = document.querySelector(`#PredictiveSearchProducts-${sid} [data-ck-predictive-products-target]`);
    const col = document.getElementById('PredictiveSearchCollections-' + sid);
    const hasNon = non && non.textContent.trim().length > 0;
    const hasProd = prodTarget && prodTarget.children.length > 0;
    const hasCol = col && col.children.length > 0;

    const emptyEl = results.querySelector('[data-ck-empty-state]');
    const moreEl = results.querySelector('[data-ck-view-more]');
    if (!emptyEl || !moreEl) return;

    emptyEl.classList.add('hidden');
    moreEl.classList.add('hidden');

    if (!hasNon && !hasProd && !hasCol) {
      emptyEl.classList.remove('hidden');
      const termsHost = emptyEl.querySelector('[data-ck-empty-terms]');
      const q = this.getQuery();
      const fmt = theme.strings && theme.strings.ckSearchNoResultsForTerms;
      if (termsHost && fmt) {
        termsHost.textContent = fmt.replace('###', q);
      } else if (termsHost) {
        termsHost.textContent = q;
      }
    } else {
      moreEl.classList.remove('hidden');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class PredictiveSearchOverlay extends OverlayElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  onClick() {
    setTimeout(() => {
      document.body.classList.remove('predictive-search-open');
    });
  }
}
customElements.define('predictive-search-overlay', PredictiveSearchOverlay);
